/**
 * The person's own text size (piece 12; decision 1321). 🔒 Two formats and no
 * third: Normal, the five sizes of the type scale (decision 1320), and Large,
 * each of those one step up. The owner, 2026-09-29: *"make 2 sizes formats for
 * normal people and old people"*.
 *
 * 🔨 The choice is a stored row, never a guess from the browser's own zoom or
 * the machine's settings, and it follows the person to every device they sign
 * in from. The frame writes it onto the document root as one data attribute,
 * and only the token file reads that attribute, so no component knows which
 * format it is drawn in.
 */

/** The key the Person-scoped SettingValue row is held under. */
export const TEXT_SIZE_KEY = 'display.textSize';

/** The two formats, in the order the settings page offers them. */
export const TEXT_SIZES = ['Normal', 'Large'] as const;
export type TextSize = (typeof TEXT_SIZES)[number];

/** A person with no row of their own reads Normal. */
export const DEFAULT_TEXT_SIZE: TextSize = 'Normal';

/** The route the person's own text size is written to, under the shell's root; the web's frame and the phone's reader both send it (decisions 1321, 11700). */
export const TEXT_SIZE_PATH = '/shell/text-size';

/** The attribute on the document root the token file's Large block is keyed on. */
export const TEXT_SIZE_ATTRIBUTE = 'data-text-size';

/**
 * The browser's copy of the person's choice, read before the frame's first
 * answer arrives so a Large reader never sees a page flash at Normal first.
 * 🔨 It is a cache and never the record: the frame's answer overwrites it on
 * every load.
 */
export const TEXT_SIZE_STORAGE_KEY = 'bms2:text-size';

export function isTextSize(value: unknown): value is TextSize {
  return typeof value === 'string' && (TEXT_SIZES as readonly string[]).includes(value);
}

/** The format in force: the person's own row where it names one, Normal otherwise. */
export function asTextSize(value: unknown): TextSize {
  return isTextSize(value) ? value : DEFAULT_TEXT_SIZE;
}

/** The attribute's value for a format; Normal writes none, so the root carries nothing extra for most people. */
export function textSizeAttributeValue(size: TextSize): string | null {
  return size === 'Large' ? 'large' : null;
}
